/* eslint-disable lingui/no-unlocalized-strings -- caminhos e rótulos técnicos de log, não texto de UI */
import type { z } from 'zod';

import { bankingApi } from '@/shared/http';

import {
  automaticSplitListQueryParams,
  automaticSplitListResponseSchema,
  automaticSplitOperationSchema,
  escrowBeneficiariesResponseSchema,
  simulateResponseSchema,
  type AutomaticSplitListParams,
  type CancelBody,
  type EscrowQuery,
  type InstallmentStatusBody,
  type SaveBody,
} from './automatic-split.wire';

// Uma função por chamada, citando `UYZY@master` (`services/automaticSplit/automaticSplit.ts`). BankingApi,
// timeout padrão (16 s, `axiosApi.ts:49`), `Authorization: Bearer` pelo interceptor. Só a lista manda `tenant`.

/** U27: fio fora do contrato só avisa fora de produção e segue com o valor cru. */
function wire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

const withSignal = (signal: AbortSignal | undefined) => (signal ? { signal } : {});
const BASE = '/BankAccount/AutomaticSplit';
const OPERATION = `${BASE}/Operation`;
const operationPath = (id: string) => `${OPERATION}/${encodeURIComponent(id)}`;

/** #1 `GET …/Operations` — `automaticSplit.ts:187-208`; query de `automaticSplitContext.tsx:132-134`. */
export async function listAutomaticSplits(params: AutomaticSplitListParams, signal?: AbortSignal) {
  const data = await bankingApi.get<unknown>(`${OPERATION}s`, { params: automaticSplitListQueryParams(params), withTenant: true, ...withSignal(signal) });
  return wire(automaticSplitListResponseSchema, data, 'GET /BankAccount/AutomaticSplit/Operations');
}

/** #2 `GET …/Operation/{id}` — `automaticSplit.ts:119-126`. O mapeamento do legado fica em `automatic-split.mappers.ts`. */
export async function getAutomaticSplit(id: string, signal?: AbortSignal) {
  const data = await bankingApi.get<unknown>(operationPath(id), { routeTemplate: `${OPERATION}/{id}`, ...withSignal(signal) });
  return wire(automaticSplitOperationSchema, data, 'GET /BankAccount/AutomaticSplit/Operation/{id}');
}

/** #3 `POST …/Operation/Simulate` — `automaticSplit.ts:62-87`. O corpo é o formulário inteiro (`AutomaticSplitFormSubContainer.tsx:43-49`). */
export async function simulateAutomaticSplit(body: Record<string, unknown>) {
  const data = await bankingApi.post(`${OPERATION}/Simulate`, body);
  return wire(simulateResponseSchema, data, 'POST /BankAccount/AutomaticSplit/Operation/Simulate');
}

/** #4 `POST …/Operation` (criar) — `automaticSplit.ts:112-116`. */
export function createAutomaticSplit(body: SaveBody) {
  return bankingApi.post(OPERATION, body);
}

/** #5 `PUT …/Operation/{id}` (editar) — `automaticSplit.ts:112-116`. */
export function updateAutomaticSplit(id: string, body: SaveBody) {
  return bankingApi.put(operationPath(id), body, { routeTemplate: `${OPERATION}/{id}` });
}

/** #6 `GET …/Escrow/Beneficiaries` — `automaticSplit.ts:210-249`: os campos do drawer da escrow na query. */
export async function listEscrowBeneficiaries(params: EscrowQuery) {
  const data = await bankingApi.get<unknown>(`${BASE}/Escrow/Beneficiaries`, { params });
  return wire(escrowBeneficiariesResponseSchema, data, 'GET /BankAccount/AutomaticSplit/Escrow/Beneficiaries');
}

/** #7 `POST …/Installment/{installmentId}/installmentStatus` — `automaticSplit.ts:251-266`. */
export function updateInstallmentStatus(installmentId: string, body: InstallmentStatusBody) {
  return bankingApi.post(`${BASE}/Installment/${encodeURIComponent(installmentId)}/installmentStatus`, body, {
    routeTemplate: `${BASE}/Installment/{installmentId}/installmentStatus`,
  });
}

/** #8 `POST …/Operation/{id}/Cancel` com `{ code, sessionId }` no corpo — `automaticSplit.ts:268-280`. */
export function cancelAutomaticSplit(id: string, body: CancelBody) {
  return bankingApi.post(`${operationPath(id)}/Cancel`, body, { routeTemplate: `${OPERATION}/{id}/Cancel` });
}
